import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, Space, Typography } from 'antd';
import { MaterialRequestDialog } from '@/components/manufacturing/MaterialRequestDialog';
import { ProjectSelector } from '@/components/project/ProjectSelector';

const { Title, Text } = Typography;

const MaterialRequestCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const workOrderId = searchParams.get('workOrderId') || searchParams.get('work_order_id') || '';
  const maintenanceCaseId = searchParams.get('maintenanceCaseId') || '';
  const projectIdParam = searchParams.get('projectId') || searchParams.get('project_id') || '';
  const context = searchParams.get('context') || '';
  const returnTo = searchParams.get('returnTo');

  const [pickedProjectId, setPickedProjectId] = useState(projectIdParam);

  const goBack = () => {
    if (returnTo) {
      navigate(returnTo);
      return;
    }
    if (maintenanceCaseId) {
      navigate(`/asset/workshop/jobs/${maintenanceCaseId}`);
      return;
    }
    if (projectIdParam || pickedProjectId) {
      navigate('/prj/projects');
      return;
    }
    if (workOrderId) {
      navigate(`/mfg/work-orders/${workOrderId}`);
      return;
    }
    navigate('/scm/inventory/material-requests');
  };

  if (workOrderId) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ marginBottom: 16 }}>
          <Title level={2} style={{ marginBottom: 0 }}>Create Material Request</Title>
          <Text type="secondary">Work order: {workOrderId}</Text>
        </div>
        <MaterialRequestDialog
          mode="page"
          workOrderId={workOrderId}
          contextNotesPrefix={context || undefined}
          onBack={goBack}
          onClose={goBack}
          onSuccess={() => navigate('/scm/inventory/material-requests')}
        />
      </div>
    );
  }

  if (maintenanceCaseId) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ marginBottom: 16 }}>
          <Title level={2} style={{ marginBottom: 0 }}>Create Material Request</Title>
          <Text type="secondary">Maintenance case: {maintenanceCaseId}</Text>
        </div>
        <MaterialRequestDialog
          mode="page"
          maintenanceCaseId={maintenanceCaseId}
          contextNotesPrefix={
            context || `Workshop maintenance case ${maintenanceCaseId}`
          }
          onBack={goBack}
          onClose={goBack}
          onSuccess={goBack}
        />
      </div>
    );
  }

  if (projectIdParam || pickedProjectId) {
    const activeProjectId = projectIdParam || pickedProjectId;
    return (
      <div style={{ padding: 24 }}>
        <div style={{ marginBottom: 16 }}>
          <Title level={2} style={{ marginBottom: 0 }}>Create Material Request</Title>
          <Text type="secondary">Project context — manual lines only</Text>
        </div>
        <MaterialRequestDialog
          mode="page"
          projectId={activeProjectId}
          onBack={goBack}
          onClose={goBack}
          onSuccess={() => navigate('/scm/inventory/material-requests')}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>New Material Request</Title>
      <Text type="secondary">Choose the fulfillment context for this request.</Text>

      <Card style={{ marginTop: 16, maxWidth: 640 }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Text strong>Work order</Text>
            <div style={{ marginTop: 8 }}>
              <Button type="link" onClick={() => navigate('/mfg/work-orders')}>
                Open Work Orders — create from a work order
              </Button>
            </div>
          </div>

          <div>
            <Text strong>Project</Text>
            <div style={{ marginTop: 8 }}>
              <div style={{ width: '100%' }}>
                <ProjectSelector
                  value={pickedProjectId || undefined}
                  onChange={(id) => setPickedProjectId(id || '')}
                />
              </div>
              <Button
                type="primary"
                style={{ marginTop: 8 }}
                disabled={!pickedProjectId}
                onClick={() =>
                  navigate(`/scm/inventory/material-requests/new?projectId=${pickedProjectId}`)
                }
              >
                Continue with project
              </Button>
            </div>
          </div>

          <div>
            <Text strong>Workshop maintenance case</Text>
            <div style={{ marginTop: 8 }}>
              <Button type="link" onClick={() => navigate('/asset/workshop')}>
                Open Workshop jobs — use Parts tab to request materials
              </Button>
            </div>
          </div>

          <Alert
            type="info"
            showIcon
            message="Store issuance is separate"
            description="After approval, store staff fulfill requests on Material Issues."
          />
        </Space>
      </Card>
    </div>
  );
};

export default MaterialRequestCreatePage;
